import { faCircleCheck, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AnimatePresence, motion } from 'motion/react';

interface BannerProps {
  title: string;
  body: string | React.ReactNode;
  visible?: boolean;
  variant?: 'error' | 'success';
}

interface SignupBannerProps {
  text: string;
  visible?: boolean;
  variant?: 'error' | 'success';
}

const ERROR_MESSAGES: {
  [key: string]: {
    title: string;
    content: React.ReactNode;
  };
} = {
  ERROR_AUTH_EXISTING_USER: {
    title: 'Unable to create an account with this email',
    content: <>An account with this email may already exist</>
  },
  ERROR_AUTH_COMPANY_UNAVAILABLE: {
    title: "This company name isn't available",
    content: <>Please try a variation or add additional details</>
  }
};

export function Banner({ title, body, visible, variant = 'error' }: BannerProps) {
  const colors = {
    error: {
      container: 'bg-utility-error-100 border-utility-error-500',
      text: 'text-utility-error-500',
      icon: faTriangleExclamation
    },
    success: {
      container: 'bg-utility-success-100 border-utility-success-500',
      text: 'text-utility-success-600',
      icon: faCircleCheck
    }
  };

  return (
    <AnimatePresence mode="wait">
      {visible && (
        <motion.div
          key={title}
          className={`text-sm border w-full rounded-2xs p-12 my-2 -indent-2 mb-15 ${colors[variant].container}`}
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -30 }}
          transition={{ duration: 0.2, type: 'spring', stiffness: 100 }}
        >
          <p
            className={`${colors[variant].text} ${variant === 'error' ? 'pb-4' : ''} font-bold flex items-center`}
          >
            <FontAwesomeIcon icon={colors[variant].icon} className="ml-2 mr-8" />
            {title}
          </p>
          {body && <p className={`${colors[variant].text} text-xs ml-2`}>{body}</p>}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export function SignupBanner({ text, visible, variant = 'error' }: SignupBannerProps) {
  const isError = variant === 'error';
  const customError = Object.keys(ERROR_MESSAGES).includes(text);

  const title = customError ? ERROR_MESSAGES[text].title : isError ? 'Something went wrong.' : text;

  const body = customError ? (
    ERROR_MESSAGES[text].content
  ) : isError ? (
    <>
      {`${text.replace(/\.\s*$/, '')}. Need help? `}
      <a href="mailto:info@scribl.co" className="underline hover:text-utility-error-500/80">
        Reach out to info@scribl.co
      </a>
    </>
  ) : null;

  return <Banner title={title} body={body} variant={variant} visible={visible} />;
}
